import { useCallback, useRef } from 'react';

import { type PartialLineItem } from '../../../../graphql';
import { useCart } from '../../../../hooks';
import { useConfirmLocation } from '../useConfirmLocation';
import { useConflictReview } from '../useConflictReview';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Same flow as `useReorderLineItem`, but the line item is passed when the
 * returned callback runs and the callback keeps its identity across renders,
 * so a list can share one callback instead of subscribing to the cart and the
 * reorder machine in every row.
 */
export const useStartReorderLineItem = () => {
  const cartState = useCart();
  const confirmLocation = useConfirmLocation();
  const conflictReview = useConflictReview();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const cartStateRef = useRef(cartState);
  const confirmLocationRef = useRef(confirmLocation);
  const conflictReviewRef = useRef(conflictReview);

  cartStateRef.current = cartState;
  confirmLocationRef.current = confirmLocation;
  conflictReviewRef.current = conflictReview;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  return useCallback(async (lineItem: PartialLineItem) => {
    const { cart, isCartEmpty, cartOrderType } = cartStateRef.current;
    const { deliveryOrderDetail, restaurant } = cart ?? {};

    if (isCartEmpty) {
      confirmLocationRef.current({
        lineItemId: lineItem?.id,
        restaurantId: lineItem?.product?.restaurant?.id,
        restaurantSlug: lineItem?.product?.restaurant?.slug,
        restaurantName: lineItem?.product?.restaurant?.name,
        orderType: 'pickup',
        userInitiated: true,
      });

      return;
    }

    void conflictReviewRef.current({
      lineItemId: lineItem?.id,
      orderType: cartOrderType,
      deliveryAddressName: deliveryOrderDetail?.address?.name ?? undefined,
      deliveryOrderDetail: deliveryOrderDetail?.address?.id
        ? {
            addressId: deliveryOrderDetail?.address?.id,
            deliveryFee: deliveryOrderDetail?.deliveryFee,
            tip: deliveryOrderDetail?.tip,
            vendor: deliveryOrderDetail?.vendor,
            vendorRestaurantId: deliveryOrderDetail?.vendorRestaurantId,
          }
        : undefined,
      restaurantId: restaurant?.id ?? '',
      restaurantSlug: restaurant?.slug ?? '',
      restaurantName: restaurant?.name ?? '',
      userInitiated: true,
    });
  }, []);
};
